import { Card, Flex } from "antd";
import { useTranslation } from "react-i18next";
import { type ReactElement } from "react";
import { Col, Row } from "syngenta-digital-cropwise-react-ui-kit";
import { formatTranslation } from "../../../../../../../shared/utils/translationUtils";
import { getProductUnit } from "../../../../../utils";

interface TreatmentDetailsCardProps {
  pIndex: number;
  item: any;
  handleRemoveTreatment?: (pIndex: number) => void;
  handleEdit?: (index: number) => void;
  summary?: boolean;
}

export const TreatmentDetailsCard = ({
  pIndex,
  item,
  handleRemoveTreatment,
  handleEdit,
  summary = false,
}: TreatmentDetailsCardProps): ReactElement => {
  const { t } = useTranslation();

  const Component = summary ? "div" : Card;
  return (
    <Component>
      <Flex justify={"space-between"} align={"center"} className="mb-3">
        <h2 className="treatment-title">
          {formatTranslation(
            t("createProtocol.products.biologicals.product.treatmentTitle")
          )}{" "}
          {pIndex + 1}
        </h2>
        {!summary && (
          <Flex justify={"flex-end"} align={"center"} gap={8}>
            <div className="pointer" onClick={() => handleEdit?.(pIndex)}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M15.3074 2.74167L17.2574 4.69167C17.5824 5.01667 17.5824 5.54167 17.2574 5.86667L15.7324 7.39167L12.6074 4.26667L14.1324 2.74167C14.2907 2.58333 14.499 2.5 14.7157 2.5C14.9324 2.5 15.1407 2.575 15.3074 2.74167ZM2.49902 14.375V17.5H5.62402L14.8407 8.28333L11.7157 5.15833L2.49902 14.375ZM4.93236 15.8333H4.16569V15.0667L11.7157 7.51667L12.4824 8.28333L4.93236 15.8333Z"
                  fill="#868CA2"
                />
              </svg>
            </div>
            <div
              className="pointer"
              onClick={() => {
                handleRemoveTreatment?.(pIndex);
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.0837 2.5H7.91699L7.08366 3.33333H4.16699V5H15.8337V3.33333H12.917L12.0837 2.5ZM13.3337 7.5V15.8333H6.66699V7.5H13.3337ZM5.00033 5.83333H15.0003V15.8333C15.0003 16.75 14.2503 17.5 13.3337 17.5H6.66699C5.75033 17.5 5.00033 16.75 5.00033 15.8333V5.83333Z"
                  fill="#EB4B4B"
                />
              </svg>
            </div>
          </Flex>
        )}
      </Flex>
      <div className="product-details">
        <Row>
          <Col span={12}>
            <div className="details-items p-r-14">
              <div className="p-header">
                {formatTranslation(
                  t(
                    "createProtocol.products.biologicals.product.chooseProductLabel"
                  )
                )}
              </div>
              <div className="p-border" />
              {item.products.map((product: any, index: number) => (
                <div className="details-item" key={index}>
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="4"
                      y="4"
                      width="20"
                      height="20"
                      rx="4"
                      fill="#FFF3DD"
                    />
                    <rect
                      x="10"
                      y="10"
                      width="8"
                      height="8"
                      rx="2"
                      fill="#F0C355"
                    />
                  </svg>
                  <div className="p-bold-text">{product.variety}</div>
                  <div className="p-desc-text">
                    {product.rate} {getProductUnit(product?.measure_unit)}
                  </div>
                </div>
              ))}
            </div>
          </Col>
          <Col span={12}>
            <div className="details-items p-l-14">
              <div className="p-header">
                {formatTranslation(
                  t("createProtocol.products.biologicals.product.tankMixRate")
                )}
              </div>
              <div className="p-border" />
              <div className="details-item">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="4"
                    width="20"
                    height="20"
                    rx="4"
                    fill="#F3F4F6"
                  />
                  <path
                    d="M17.5 15C17.5 14.4726 17.2395 13.6109 16.8606 13C15.8508 11.3718 14 9.5 14 9.5C14 9.5 12.1492 11.3718 11.1394 13C10.7605 13.6109 10.5 14.4726 10.5 15C10.5 16.933 12.067 18.5 14 18.5C15.933 18.5 17.5 16.933 17.5 15Z"
                    fill="#868CA2"
                  />
                </svg>
                <div className="p-bold-text">
                  {formatTranslation(t("createProtocol.products.min"))}{" "}
                  <span className="p-desc-text">
                    {item?.tank_mix_rate?.min} L/ha{" "}
                  </span>
                </div>
                <div className="p-dot" />
                <div className="p-bold-text">
                  {formatTranslation(t("createProtocol.products.max"))}{" "}
                  <span className="p-desc-text">
                    {item?.tank_mix_rate?.max} L/ha{" "}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          <Col span={24}>
            <div className="product-details">
              <div className="details-items">
                <div className="p-header">
                  {formatTranslation(
                    t(
                      "createProtocol.products.biologicals.product.instructions"
                    )
                  )}
                </div>
                {!summary && <div className="p-border" />}
                <div className="p-desc-text">{item?.instructions}</div>
                {summary && (
                  <div className="p-border" style={{ marginTop: "20px" }} />
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Component>
  );
};
